'use client';

import { useState, useEffect } from 'react';
import FilterControls from './FilterControls';
import TopThreeShowcase from './TopThreeShowcase';
import LeaderboardList from './LeaderboardList';
import ContributorStats from './ContributorStats';

interface FilterState {
  timePeriod: string;
  contributionType: string;
  questCategory: string;
}

interface Contributor {
  id: number;
  rank: number;
  name: string;
  avatar: string;
  alt: string;
  totalContributions: number;
  questsCompleted: number;
  contentSubmissions: number;
  communityHelp: number;
  joinedDate: string;
  currentStreak: number;
  badges: string[];
  specialTitle: string;
  recentActivity: string;
  trendDirection: 'up' | 'down' | 'stable';
  rankChange: number;
}

interface LeaderboardInteractiveProps {
  initialContributors: Contributor[];
  initialStats: {
    totalContributions: number;
    activeThisWeek: number;
    questsCompleted: number;
    contentSubmitted: number;
    averageContributions: number;
    topContributorStreak: number;
  };
}

export default function LeaderboardInteractive({ initialContributors, initialStats }: LeaderboardInteractiveProps) {
  const [isHydrated, setIsHydrated] = useState(false);
  const [contributors, setContributors] = useState<Contributor[]>(initialContributors);
  const [filteredContributors, setFilteredContributors] = useState<Contributor[]>(initialContributors);
  const [isLoading, setIsLoading] = useState(false);
  const [stats, setStats] = useState(initialStats);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const handleFilterChange = (filters: FilterState) => {
    if (!isHydrated) return;
    
    setIsLoading(true);
    
    // Simulate API call delay
    setTimeout(() => {
      let filtered = [...contributors];
      
      // Apply time period filter
      if (filters.timePeriod !== 'all-time') {
        // In a real app, this would filter based on actual dates
        filtered = filtered.slice(0, Math.floor(filtered.length * 0.7));
      }
      
      // Apply contribution type filter
      if (filters.contributionType !== 'all') {
        // In a real app, this would filter based on contribution types
        filtered = filtered.filter(c => c.totalContributions > 100);
      }
      
      // Apply quest category filter
      if (filters.questCategory !== 'all') {
        // In a real app, this would filter based on quest categories
        filtered = filtered.filter(c => c.questsCompleted > 10);
      }
      
      setFilteredContributors(filtered);
      setIsLoading(false);
    }, 500);
  };

  if (!isHydrated) {
    return (
      <div data-component-id="src/app/top-contributors-leaderboard/components/LeaderboardInteractive.tsx:91:6" data-component-path="src/app/top-contributors-leaderboard/components/LeaderboardInteractive.tsx" data-component-line="91" data-component-file="LeaderboardInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-8%22%7D" className="space-y-8">
        <div data-component-id="src/app/top-contributors-leaderboard/components/LeaderboardInteractive.tsx:92:8" data-component-path="src/app/top-contributors-leaderboard/components/LeaderboardInteractive.tsx" data-component-line="92" data-component-file="LeaderboardInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-card%20rounded-lg%20border%20border-border%20p-6%20animate-pulse%22%7D" className="bg-card rounded-lg border border-border p-6 animate-pulse">
          <div data-component-id="src/app/top-contributors-leaderboard/components/LeaderboardInteractive.tsx:93:10" data-component-path="src/app/top-contributors-leaderboard/components/LeaderboardInteractive.tsx" data-component-line="93" data-component-file="LeaderboardInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-8%20bg-muted%20rounded%20w-1%2F3%20mb-4%22%7D" className="h-8 bg-muted rounded w-1/3 mb-4"></div>
          <div data-component-id="src/app/top-contributors-leaderboard/components/LeaderboardInteractive.tsx:94:10" data-component-path="src/app/top-contributors-leaderboard/components/LeaderboardInteractive.tsx" data-component-line="94" data-component-file="LeaderboardInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-4%20bg-muted%20rounded%20w-2%2F3%22%7D" className="h-4 bg-muted rounded w-2/3"></div>
        </div>
        <div data-component-id="src/app/top-contributors-leaderboard/components/LeaderboardInteractive.tsx:96:8" data-component-path="src/app/top-contributors-leaderboard/components/LeaderboardInteractive.tsx" data-component-line="96" data-component-file="LeaderboardInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-card%20rounded-lg%20border%20border-border%20p-6%20animate-pulse%22%7D" className="bg-card rounded-lg border border-border p-6 animate-pulse">
          <div data-component-id="src/app/top-contributors-leaderboard/components/LeaderboardInteractive.tsx:97:10" data-component-path="src/app/top-contributors-leaderboard/components/LeaderboardInteractive.tsx" data-component-line="97" data-component-file="LeaderboardInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-64%20bg-muted%20rounded%22%7D" className="h-64 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  const topThree = filteredContributors.slice(0, 3);

  return (
    <div data-component-id="src/app/top-contributors-leaderboard/components/LeaderboardInteractive.tsx:106:4" data-component-path="src/app/top-contributors-leaderboard/components/LeaderboardInteractive.tsx" data-component-line="106" data-component-file="LeaderboardInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-8%22%7D" className="space-y-8">
      <ContributorStats data-component-id="src/app/top-contributors-leaderboard/components/LeaderboardInteractive.tsx:107:6" data-component-path="src/app/top-contributors-leaderboard/components/LeaderboardInteractive.tsx" data-component-line="107" data-component-file="LeaderboardInteractive.tsx" data-component-name="ContributorStats" data-component-content="%7B%22elementName%22%3A%22ContributorStats%22%2C%22%5Bspread%5D%22%3A%22true%22%7D" {...stats} />
      
      <FilterControls data-component-id="src/app/top-contributors-leaderboard/components/LeaderboardInteractive.tsx:109:6" data-component-path="src/app/top-contributors-leaderboard/components/LeaderboardInteractive.tsx" data-component-line="109" data-component-file="LeaderboardInteractive.tsx" data-component-name="FilterControls" data-component-content="%7B%22elementName%22%3A%22FilterControls%22%7D" onFilterChange={handleFilterChange} />
      
      {topThree.length >= 3 && <TopThreeShowcase data-component-id="src/app/top-contributors-leaderboard/components/LeaderboardInteractive.tsx:111:31" data-component-path="src/app/top-contributors-leaderboard/components/LeaderboardInteractive.tsx" data-component-line="111" data-component-file="LeaderboardInteractive.tsx" data-component-name="TopThreeShowcase" data-component-content="%7B%22elementName%22%3A%22TopThreeShowcase%22%7D" topThree={topThree} />}
      
      <LeaderboardList data-component-id="src/app/top-contributors-leaderboard/components/LeaderboardInteractive.tsx:113:6" data-component-path="src/app/top-contributors-leaderboard/components/LeaderboardInteractive.tsx" data-component-line="113" data-component-file="LeaderboardInteractive.tsx" data-component-name="LeaderboardList" data-component-content="%7B%22elementName%22%3A%22LeaderboardList%22%7D" 
        contributors={filteredContributors.slice(3)} 
        isLoading={isLoading}
      />
    </div>
  );
}